import React, { useEffect, useMemo } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useAppSelector } from './redux/hooks';
import SidebarLayout from './layouts/SidebarLayout';
import PageNotFound from './components/PageNotFound';
import RequirePermission from 'src/components/RequirePermission';
import PermissionService, { ACTION, ROLE } from './services/permission-service';
import {
  Warehouse as WarehouseIcon,
  Business as SupplierIcon,
  Security as SecurityIcon,
  Settings as LookupsIcon,
  Flag as FlagIcon,
  LocationCity as LocationCityIcon,
  AccountTree as ProjectIcon,
  People as UsersIcon,
  Assignment as RolesIcon,
  Badge as IdentificationIcon,
  Payment as PaymentIcon,
  Home as HomeIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  Devices as DevicesIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  PersonOutline as TenantsIcon,
  Dashboard as DashboardIcon,
  Description as LeaseIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as SecurityDepositIcon,
  Message as ComplaintsIcon
} from '@mui/icons-material';
import Dashboard from './pages/Dashboard/Dashboard';
import ClientListing from './pages/Clients/Clients-listing';
import CountriesListing from './pages/Lookups/Countries-listing';
import CitiesListing from './pages/Lookups/Cities-listing';
import IdentificationTypesListing from './pages/Lookups/IdentificationTypes-listing';
import PaymentTypesListing from './pages/Lookups/PaymentTypes-listing';
import PropertyTypesListing from './pages/Lookups/PropertyTypes-listing';
import RentCyclesListing from './pages/Lookups/RentCycles-listing';
import FeeTypesListing from './pages/Lookups/FeeTypes-listing';
import AppliancesListing from './pages/Lookups/Appliances-listing';
import CurrenciesListing from './pages/Lookups/Currencies-listing';
import TenantsListing from './pages/Tenants/Tenants-listing';
import TenantDetailsPage from './pages/Tenants/TenantDetailsPage';
import ProjectsListing from './pages/Projects/Projects-listing';
import PropertiesListing from './pages/Properties/Properties-listing';
import RentalUnitsListing from './pages/Units/RentalUnits-listing';
import LeasesListing from './pages/Leases/Leases-listing';
import LeaseDetailsPage from './pages/Leases/LeaseDetailsPage';
import ReceiptsListing from './pages/Receipts/Receipts-listing';
import ReceiptGroupDetail from './pages/Receipts/ReceiptGroupDetail';
import SecurityDepositsListing from './pages/SecurityDeposits/SecurityDeposits-listing';
import UsersListing from './pages/Users/Users-listing';
import RolesListing from './pages/Users/Roles-listing';
import ComplaintsListing from './pages/Complaints/ComplaintsListing';
import ComplaintThreadDetailPage from './pages/Complaints/ComplaintThreadDetail';

interface RouteConfig {
  path: string;
  element: React.ReactElement;
  title: string;
  permission?: ACTION;
  exact?: boolean;
}

const routeConfigs: RouteConfig[] = [
  {
    path: '/',
    element: <Dashboard />,
    title: 'Dashboard',
    exact: true
  },
  {
    path: '/dashboard',
    element: <Dashboard />,
    title: 'Dashboard',
    exact: true
  },
  {
    path: '/clients',
    element: (
      <RequirePermission action={ACTION.VIEW_ALL_CLIENTS}>
        <ClientListing />
      </RequirePermission>
    ),
    title: 'Supplier Management',
    permission: ACTION.VIEW_ALL_SUPPLIERS
  },
  {
    path: '/countries',
    element: <CountriesListing />,
    title: 'Countries Management',
    exact: true
  },
  {
    path: '/cities',
    element: <CitiesListing />,
    title: 'Cities Management',
    exact: true
  },
  {
    path: '/identification-types',
    element: <IdentificationTypesListing />,
    title: 'Identification Types Management',
    exact: true
  },
  {
    path: '/payment-types',
    element: <PaymentTypesListing />,
    title: 'Payment Types Management',
    exact: true
  },
  {
    path: '/property-types',
    element: <PropertyTypesListing />,
    title: 'Property Types Management',
    exact: true
  },
  {
    path: '/billing-cycles',
    element: <RentCyclesListing />,
    title: 'Billing Cycles Management',
    exact: true
  },
  {
    path: '/fee-types',
    element: <FeeTypesListing />,
    title: 'Fee Types Management',
    exact: true
  },
  {
    path: '/appliances',
    element: <AppliancesListing />,
    title: 'Appliances Management',
    exact: true
  },
  {
    path: '/currencies',
    element: <CurrenciesListing />,
    title: 'Currencies Management',
    exact: true
  },
  {
    path: '/tenants',
    element: <TenantsListing />,
    title: 'Tenants Management',
    exact: true
  },
  {
    path: '/tenants/:tenantId',
    element: <TenantDetailsPage />,
    title: 'Tenant Details',
    exact: true
  },
  {
    path: '/projects',
    element: <ProjectsListing />,
    title: 'Projects Management',
    exact: true
  },
  {
    path: '/properties',
    element: <PropertiesListing />,
    title: 'Properties Management',
    exact: true
  },
  {
    path: '/rental-units',
    element: <RentalUnitsListing />,
    title: 'Rental Units Gallery',
    exact: true
  },
  {
    path: '/leases',
    element: <LeasesListing />,
    title: 'Lease Management',
    exact: true
  },
  {
    path: '/leases/:leaseId',
    element: <LeaseDetailsPage />,
    title: 'Lease Details',
    exact: true
  },
  {
    path: '/receipts',
    element: <ReceiptsListing />,
    title: 'Receipts Management',
    exact: true
  },
  {
    path: '/receipt-groups/:refNumber',
    element: <ReceiptGroupDetail />,
    title: 'Receipt Group Details',
    exact: true
  },
  {
    path: '/security-deposits',
    element: <SecurityDepositsListing />,
    title: 'Security Deposits Management',
    exact: true
  },
  {
    path: '/users',
    element: <UsersListing />,
    title: 'User Management',
    exact: true
  },
  {
    path: '/roles',
    element: <RolesListing />,
    title: 'Role Management',
    exact: true
  },
  {
    path: '/complaints',
    element: <ComplaintsListing />,
    title: 'Tenant Complaints',
    exact: true
  },
  {
    path: '/complaints/:threadId',
    element: <ComplaintThreadDetailPage />,
    title: 'Complaint Details',
    exact: true
  }
];

interface SidebarCategory {
  id: string;
  label: string;
  order: number;
}

const SIDEBAR_CATEGORIES: SidebarCategory[] = [
  { id: 'dashboard', label: 'Dashboard', order: 1 },
  { id: 'operations', label: 'Operations', order: 2 },
  { id: 'partners', label: 'Business Partners', order: 3 },
  { id: 'lookups', label: 'Lookups', order: 4 },
  { id: 'administration', label: 'Administration', order: 5 }
];

export const Protected: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.user);
  const isAuthed = user.isAuthenticated;

  useEffect(() => {
    if (!isAuthed) {
      navigate('/login', { replace: true });
    }
  }, [isAuthed, navigate]);

  const sidebarLinks = useMemo(() => {
    const role = user.user?.role as ROLE;
    const perm = PermissionService.getInstance();

    const links = [
      {
        id: 'dashboard',
        icon: <DashboardIcon />,
        label: 'Dashboard',
        route: '/dashboard',
        visible: true,
        category: 'dashboard',
        order: 1
      },
      {
        id: 'operations',
        icon: <WarehouseIcon />,
        label: 'Assets',
        visible: true,
        category: 'operations',
        order: 1,
        children: [
          {
            id: 'projects',
            icon: <ProjectIcon />,
            label: 'Projects',
            route: '/projects',
            visible: true
          },
          {
            id: 'properties',
            icon: <HomeIcon />,
            label: 'Properties',
            route: '/properties',
            visible: true
          },
          {
            id: 'rental-units',
            icon: <HomeIcon />,
            label: 'Units Gallery',
            route: '/rental-units',
            visible: true
          },
          {
            id: 'leases',
            icon: <LeaseIcon />,
            label: 'Leases',
            route: '/leases',
            visible: true
          },
          {
            id: 'receipts',
            icon: <ReceiptIcon />,
            label: 'Receipts',
            route: '/receipts',
            visible: true
          },
          {
            id: 'security-deposits',
            icon: <SecurityDepositIcon />,
            label: 'Security Deposits',
            route: '/security-deposits',
            visible: true
          },
        ]
      },

      {
        id: 'partners',
        icon: <SupplierIcon />,
        label: 'Business Partners',
        visible: true,
        category: 'partners',
        order: 1,
        children: [
          perm.isAllowed(ACTION.VIEW_ALL_SUPPLIERS, role) && {
            id: 'suppliers',
            icon: <SupplierIcon />,
            label: 'Suppliers',
            route: '/suppliers',
            visible: true
          },
          perm.isAllowed(ACTION.VIEW_ALL_CLIENTS, role) && {
            id: 'clients',
            icon: <SupplierIcon />,
            label: 'Clients',
            route: '/clients',
            visible: true
          },
          {
            id: 'tenants',
            icon: <TenantsIcon />,
            label: 'Tenants',
            route: '/tenants',
            visible: true
          },
           {
            id: 'complaints',
            icon: <ComplaintsIcon />,
            label: 'Complaints',
            route: '/complaints',
            visible: true
          }
        ].filter(Boolean)
      },

      {
        id: 'lookups',
        icon: <LookupsIcon />,
        label: 'Lookups',
        visible: true,
        category: 'lookups',
        order: 1,
        children: [
          {
            id: 'countries',
            icon: <FlagIcon />,
            label: 'Countries',
            route: '/countries',
            visible: true
          },
          {
            id: 'cities',
            icon: <LocationCityIcon />,
            label: 'Cities',
            route: '/cities',
            visible: true
          },
          {
            id: 'identification-types',
            icon: <IdentificationIcon />,
            label: 'Identification Types',
            route: '/identification-types',
            visible: true
          },
          {
            id: 'payment-types',
            icon: <PaymentIcon />,
            label: 'Payment Types',
            route: '/payment-types',
            visible: true
          },
          {
            id: 'property-types',
            icon: <HomeIcon />,
            label: 'Property Types',
            route: '/property-types',
            visible: true
          },
          // {
          //   id: 'billing-cycles',
          //   icon: <AccessTimeIcon />,
          //   label: 'Billing Cycles',
          //   route: '/billing-cycles',
          //   visible: true
          // },
          {
            id: 'fee-types',
            icon: <AttachMoneyIcon />,
            label: 'Fee Types',
            route: '/fee-types',
            visible: true
          },
          {
            id: 'appliances',
            icon: <DevicesIcon />,
            label: 'Appliances',
            route: '/appliances',
            visible: true
          },
          {
            id: 'currencies',
            icon: <CurrencyExchangeIcon />,
            label: 'Currencies',
            route: '/currencies',
            visible: true
          }
        ]
      },

      {
        id: 'administration',
        icon: <SecurityIcon />,
        label: 'Administration',
        visible: true,
        category: 'administration',
        order: 1,
        children: [
          {
            id: 'users',
            icon: <UsersIcon />,
            label: 'Users',
            route: '/users',
            visible: true
          },
          {
            id: 'roles',
            icon: <RolesIcon />,
            label: 'Roles',
            route: '/roles',
            visible: true
          }
        ]
      }
    ].filter(Boolean);

    return links.sort((a, b) => {
      const catA =
        SIDEBAR_CATEGORIES.find((c) => c.id === a.category)?.order ?? 999;
      const catB =
        SIDEBAR_CATEGORIES.find((c) => c.id === b.category)?.order ?? 999;
      if (catA !== catB) return catA - catB;
      return (a.order ?? 999) - (b.order ?? 999);
    });
  }, [user.user?.role]);

  const layoutConfig = useMemo(
    () => ({
      sidebarWidth: { collapsed: 72, expanded: 300 },
      headerHeight: 64,
      showTabs: false,
      showUserInfo: true,
      allowSidebarToggle: true,
      showSearchBar: false,
      showNotifications: false,
      theme: 'dark' as 'dark'
    }),
    []
  );

  const enhancedUser = useMemo(
    () => ({
      name: user.user?.name || 'Unknown User',
      email: user.user?.email || '',
      role: user.user?.role || 'user',
      initials: user.user?.name
        ? user.user.name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
        : 'UN'
    }),
    [user.user]
  );

  if (!isAuthed) {
    return null;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <SidebarLayout
            links={sidebarLinks}
            user={enhancedUser}
            config={layoutConfig}
          />
        }
      >
        {routeConfigs.map((routeConfig, index) => (
          <Route
            key={`${routeConfig.path}-${index}`}
            path={routeConfig.path}
            element={routeConfig.element}
          />
        ))}
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Routes>
  );
};
